import { Controller, useFormContext } from 'react-hook-form';
import { FormControlLabel, FormHelperText, Link, MenuItem, Select, Typography } from '@mui/material';
import { DatasetCreationEnum } from '~/api/types';
import { useGetDatasetEnumsQuery } from '~/api/v2/hooks';
import { InfoFormLabel, SelectPlaceholder } from '~/components';
import { useHasLockedStatus } from '~/hooks/dataset';
import { dataClassificationTooltipStyles } from './styles';

const dataClassifications: {
  title: string;
  description: string;
  link?: { href: string; label: string };
}[] = [
  {
    title: 'Highly Sensitive',
    description: 'Severe harm if exposed; strictest controls (e.g., trade secrets, patient data, credentials).',
  },
  {
    title: 'Sensitive',
    description: 'Severe harm if exposed; need-to-know access (e.g., pre-market data, privileged info).',
  },
  {
    title: 'Internal Use Only',
    description: 'For Medtronic personnel; some harm if exposed (e.g., internal policies, training).',
  },
  {
    title: 'Public',
    description: 'Approved for external release; no restrictions.',
    link: {
      href: 'https://medtronic.sharepoint.com/sites/GSO/Lists/PoliciesStandards/DispForm.aspx?ID=16&e=VXcnTI',
      label: 'Learn more about data classification.',
    },
  },
];

const DataClassificationField = () => {
  const { control } = useFormContext();
  const { data, isFetching } = useGetDatasetEnumsQuery({ enumKey: DatasetCreationEnum.DATA_CLASSIFICATION });
  const { hasLockedStatus } = useHasLockedStatus();

  return (
    <FormControlLabel
      label={
        <InfoFormLabel
          label="DATA CLASSIFICATION"
          tooltipText={
            <Typography>
              <ul style={dataClassificationTooltipStyles}>
                {dataClassifications.map(({ title, description, link }) => (
                  <li key={title}>
                    <b>{title}</b> - {description}
                    {link && (
                      <>
                        {' '}
                        <Link
                          style={{ textDecoration: 'none', color: '#1010EB' }}
                          target="_blank"
                          rel="noreferrer"
                          href={link.href}
                        >
                          {link.label}
                        </Link>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </Typography>
          }
        />
      }
      required
      control={
        <Controller
          name="data_classification"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <FormHelperText error={!!fieldState.error}>{fieldState.error?.message as string}</FormHelperText>
              <Select
                {...field}
                value={field.value ?? ''}
                disabled={isFetching || hasLockedStatus}
                error={!!fieldState.error}
                renderValue={() => {
                  if (!field.value) return <SelectPlaceholder>Select the classification</SelectPlaceholder>;

                  return field.value;
                }}
              >
                {data?.map(({ value }) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
            </>
          )}
        />
      }
    />
  );
};

export default DataClassificationField;
